import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { KeySetToUppercaseString } from "../components/Editor/tools/useButtonHeld";

export interface RubberLineData {
  snapPoints: Point2d[],
  pointerPoints: Point2d[],
  endOfInput: boolean
}
interface InteractionState {
  obj?: GeometricShape,
  rubberlineData: RubberLineData,
  snappingPoints: Point2d[],
  numberInput: { distance: Point3d }
  inputFieldIndex: number,
  selectedInputLine: number,
  editorState: EditorState,
  editingReferencePoint: Point2d | undefined,
  toolMessage: string,
  button: {
    buttonHeld: { [key in KeySetToUppercaseString]: boolean }
  }
  UIInteraction: {
    saveInProgress: boolean,
    loadingInProgress: boolean,
  }
  layerEditorModal: {
    open: boolean,
    layerList: Layer[],
    deleteLayerAndContentId: number[],
    deleteLayerAndMoveToIds: { from: number, to: number }[]
  }


//todo: clean the stuff above
  operation: string;
}

const initialState: InteractionState = {
  obj: undefined,
  rubberlineData: {
    snapPoints: [] as Point2d[],
    pointerPoints: [] as Point2d[], endOfInput: false
  },
  snappingPoints: [],
  numberInput: { distance: { x: 0, y: 0, z: 0 } as Point3d },
  inputFieldIndex: 0,
  selectedInputLine: 0,
  editorState: 'normal',
  editingReferencePoint: undefined,
  toolMessage: "",
  UIInteraction: {
    saveInProgress: false,
    loadingInProgress: true,
  },
  layerEditorModal: {
    open: false,
    layerList: [],
    deleteLayerAndContentId: [],
    deleteLayerAndMoveToIds: []
  },
  button: {
    buttonHeld: {
      x: false,
      y: false,
      shift: false,
      ctrl: false
    }
  },

  //todo: clear the stuff above
  operation: "none",
}

export const interactionSlice = createSlice({
  name: 'interaction',
  initialState,

  reducers: {
    interactionAddEditingReferencePoint: (state, action: PayloadAction<Point2d | undefined>) => {
      state.editingReferencePoint = action.payload
    },
    interactionRemoveEditingReferencePoint: (state) => {
      state.editingReferencePoint = undefined;
    },
    interactionSetKeyHeld: (state, action: PayloadAction<{ key: KeySetToUppercaseString, isHeld: boolean }>) => {
      state.button.buttonHeld[action.payload.key] = action.payload.isHeld;
    },
    setObj: (state, action: PayloadAction<GeometricShape | undefined>) => {
      state.obj = action.payload;
      state.editorState = action.payload === undefined ? 'normal' : 'userInput';
    },
    interactionSetEditorState: (state, action: PayloadAction<EditorState>) => {
      console.log("change state", action.payload);

      state.editorState = action.payload;
    },
    addRubberlinePoint: (state, action: PayloadAction<{ snapPoint?: Point2d, pointerPoint?: Point2d, endOfInput: boolean }>) => {
      if (action.payload.snapPoint) state.rubberlineData.snapPoints.push(action.payload.snapPoint);
      if (action.payload.pointerPoint) state.rubberlineData.pointerPoints.push(action.payload.pointerPoint);
      state.rubberlineData.endOfInput = action.payload.endOfInput;
      state.editorState = action.payload === undefined ? 'normal' : state.editorState;
      console.log("addRubberlinePoint", state.editorState);
    },
    removeLastRubberlinePoint: (state, action: PayloadAction<{ removeNumber: number }>) => {
      for (let i = 0; i < action.payload.removeNumber; i++) {
        state.rubberlineData.snapPoints.pop();
      }
      if (state.rubberlineData.snapPoints.length === 0) {
        interactionSlice.caseReducers.endRubberline(state);
      }
    },
    stepBackRubberLine: (state, action: PayloadAction<number>) => {
      const lineData = state.rubberlineData.snapPoints;

      if (action.payload <= lineData.length) {
        state.rubberlineData.snapPoints = lineData.slice(0, lineData.length - action.payload);
        if (action.payload > lineData.length || action.payload < 0) {
          throw new Error("backstep number is not valid");
        }
      }
      if (state.rubberlineData.snapPoints.length === 0) {
        interactionSlice.caseReducers.endRubberline(state);
      }
    },
    endRubberline: (state) => {
      state.rubberlineData = { snapPoints: [], pointerPoints: [], endOfInput: false }; //set back to default
      state.editorState = 'normal';
    },
    setToolMessage: (state, action) => {
      state.toolMessage = action.payload;
    },
    interactionFloatingPaletteInput: (state, action: PayloadAction<Point2d>) => {
      state.numberInput.distance = { ...state.numberInput.distance, ...action.payload }
    },
    interactionFloatingPaletteInputEnd: (state) => {
      state.editorState = "normal";
      state.numberInput.distance = { x: 0, y: 0, z: 0 };
      state.selectedInputLine = 0;
    },
    interactionFloatingPaletteInputToggle: (state, action: PayloadAction<number | undefined>) => {
      if (action.payload) {
        state.selectedInputLine = action.payload;
      } else {
        switch (state.selectedInputLine) {
          case 0:
            state.selectedInputLine = 1
            break
          case 1:
            state.selectedInputLine = 0
            break
          default:
            break;
        }
      }
    },
    setSending: (state, action) => {
      state.UIInteraction.saveInProgress = action.payload;
    },
    setLoading: (state, action) => {
      state.UIInteraction.loadingInProgress = action.payload;
    },
    setLayerModalOpen: (state, action) => {
      state.layerEditorModal.open = action.payload;
    },
    addLayerModalLayer: (state, action: PayloadAction<Layer>) => {
      const idList = state.layerEditorModal.layerList.map(a => a.id);
      const maxId = Math.max(...idList);
      state.layerEditorModal.layerList = [
        ...state.layerEditorModal.layerList,
        { ...action.payload, id: maxId + 1, visibility: true }
      ]
    },
    toggleLayerModalOpen: (state) => {
      state.layerEditorModal.open = !state.layerEditorModal.open;
    },
    setLayerModalLayers: (state, action: PayloadAction<Layer[]>) => {
      state.layerEditorModal.layerList = action.payload;
    },
    addLayerModalDeleteLayer: (state, action: PayloadAction<number>) => {
      state.layerEditorModal.deleteLayerAndContentId.push();
    },
    addLayerModalDeleteMoveLayer: (state, action: PayloadAction<{ from: number, to: number }>) => {
      state.layerEditorModal.deleteLayerAndMoveToIds.push(action.payload);
    },
    resetLayerModal: (state, action: PayloadAction<Layer[]>) => {
      interactionSlice.caseReducers.setLayerModalLayers(state, action)
      state.layerEditorModal.deleteLayerAndMoveToIds = [];
      state.layerEditorModal.deleteLayerAndContentId = [];
    },

    setOperation: (state, action: PayloadAction<string>) => {
      state.operation = action.payload;
    },
  }
})
export const {
  interactionSetEditorState,
  interactionAddEditingReferencePoint,
  interactionSetKeyHeld,
  setObj,
  addRubberlinePoint,
  endRubberline,
  setToolMessage,
  interactionFloatingPaletteInput,
  interactionFloatingPaletteInputEnd,
  interactionFloatingPaletteInputToggle,
  removeLastRubberlinePoint,
  stepBackRubberLine,
  setSending,
  setLoading,
  setLayerModalOpen,
  toggleLayerModalOpen,
  setLayerModalLayers,
  addLayerModalLayer,
  addLayerModalDeleteLayer,
  addLayerModalDeleteMoveLayer,
  resetLayerModal,


  setOperation,

} = interactionSlice.actions

export default interactionSlice.reducer